import React, {
    createContext,
    useContext,
    useMemo,
    useState,
    ReactNode,
    useEffect,
} from 'react';
import { ThemeProvider, createTheme, Theme } from '@mui/material';

type ThemeMode = 'light' | 'dark';

export interface ThemeContextValue {
    setMode: (mode: ThemeMode) => void;
    setPrimaryColor: (color: string) => void;
    setBackgroundColor: (color: string) => void;
    theme: Theme;
}

function toOpaqueColor(color: string): string {
    const modernRgb = color.match(
        /^rgb\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*\/\s*[\d.]+\s*\)$/i,
    );
    if (modernRgb) return `rgb(${modernRgb[1]} ${modernRgb[2]} ${modernRgb[3]})`;
    const legacyRgba = color.match(
        /^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*[\d.]+\s*\)$/i,
    );
    if (legacyRgba) return `rgb(${legacyRgba[1]}, ${legacyRgba[2]}, ${legacyRgba[3]})`;
    return color;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const CustomThemeProvider = ({ children }: { children: ReactNode }) => {
    const [mode, setMode] = useState<ThemeMode>('dark');
    const [primaryColor, setPrimaryColor] = useState('#F18537');
    const [backgroundColor, setBackgroundColor] = useState(
        'rgb(17 24 39 / 0.9)',
    );

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: mode,
                    primary: {
                        main: primaryColor,
                    },
                    secondary: {
                        main: '#373736',
                    },
                    background: {
                        default: toOpaqueColor(backgroundColor),
                        paper: toOpaqueColor(backgroundColor),
                    },
                },
                typography: {
                    h1: {
                        fontSize: '2.25rem',
                        lineHeight: '2.5rem',
                        fontWeight: 700,
                    },
                    h2: {
                        fontSize: '1.5rem',
                        fontWeight: 700,
                    },
                    h3: {
                        fontSize: '1.25rem',
                        fontWeight: 700,
                    },
                },
            }),
        [mode, primaryColor],
    );

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty(
            '--color-text',
            mode === 'dark' ? '#fff' : '#000',
        );
        root.style.setProperty(
            '--color-border-light',
            mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(133, 133, 133, 1)',
        );
        root.style.setProperty(
            '--color-border-input',
            mode === 'dark'
                ? 'rgba(255, 255, 255, 0.23)'
                : 'rgba(0, 0, 0, 0.6)',
        );
        root.style.setProperty('--color-primary', theme.palette.primary.main);
        root.style.setProperty(
            '--color-background',
            theme.palette.background.default,
        );
    }, [theme]);

    const value: ThemeContextValue = {
        setMode,
        setPrimaryColor,
        setBackgroundColor,
        theme,
    };

    return (
        <ThemeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeContext = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx)
        throw new Error('useThemeMode must be used within CustomThemeProvider');
    return ctx;
};
